/**
 * 设备信息请求头构造（Web 端）
 *
 * 用途：安全合规溯源。所有请求统一携带设备标识、平台、系统等信息，
 * 服务端据此记录登录日志与设备档案，用于账号被盗申诉、违规内容溯源、监管调证。
 *
 * 设备标识策略：
 *   浏览器没有硬件级唯一标识，这里采用"随机 UUID + 本地持久化"的方案，
 *   同时写入 localStorage 与 cookie，任一存储被清理都能从另一处恢复，
 *   兼顾稳定性与隐私（不做 canvas / 字体等指纹采集，不涉及硬件信息）。
 *
 * 注意：HTTP 请求头只允许 ISO-8859-1 字符，可能出现的非 ASCII 值统一
 * encodeURIComponent 后再发送，服务端会做 URL 解码。
 */

const DEVICE_ID_KEY = 'pp_device_id'
const DEVICE_ID_MAX_AGE = 60 * 60 * 24 * 365 * 2 // 2 年

let cachedHeaders = null

function generateUuid() {
  // 优先使用浏览器原生的加密安全随机数
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID().replace(/-/g, '')
  }
  if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    window.crypto.getRandomValues(bytes)
    return Array.prototype.map.call(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  return Date.now().toString(16) + Math.random().toString(16).slice(2, 14)
}

function readCookie(name) {
  const matched = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return matched ? decodeURIComponent(matched[1]) : ''
}

function writeCookie(name, value) {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${DEVICE_ID_MAX_AGE}; SameSite=Lax`
  } catch (e) {
    // ignore
  }
}

/**
 * 获取稳定的设备标识，localStorage 与 cookie 互为备份
 */
export function getStableDeviceId() {
  let deviceId = ''

  try {
    deviceId = window.localStorage.getItem(DEVICE_ID_KEY) || ''
  } catch (e) {
    deviceId = ''
  }

  if (!deviceId) {
    deviceId = readCookie(DEVICE_ID_KEY)
  }

  if (!deviceId) {
    deviceId = 'web-' + generateUuid()
  }

  try {
    window.localStorage.setItem(DEVICE_ID_KEY, deviceId)
  } catch (e) {
    // 隐私模式下 localStorage 可能不可写，退化为仅 cookie
  }
  writeCookie(DEVICE_ID_KEY, deviceId)

  return deviceId
}

function safeHeaderValue(value) {
  if (value === null || value === undefined || value === '') {
    return ''
  }
  const str = String(value)
  // eslint-disable-next-line no-control-regex
  if (/^[\x20-\x7E]*$/.test(str)) {
    return str
  }
  try {
    return encodeURIComponent(str)
  } catch (e) {
    return ''
  }
}

/**
 * 从 UA 判断是移动端浏览器还是桌面浏览器
 */
function detectDeviceType(ua) {
  return /Android|iPhone|iPad|iPod|Windows Phone|HarmonyOS|Mobile/i.test(ua) ? 'MOBILE' : 'PC'
}

/**
 * 解析操作系统版本，用于溯源时区分同一账号的不同终端
 */
function detectOsVersion(ua) {
  const rules = [
    [/Windows NT ([\d.]+)/, 'Windows NT $1'],
    [/Mac OS X ([\d_.]+)/, 'Mac OS X $1'],
    [/Android ([\d.]+)/, 'Android $1'],
    [/(?:iPhone|CPU) OS ([\d_]+)/, 'iOS $1']
  ]
  for (const [pattern, template] of rules) {
    const matched = ua.match(pattern)
    if (matched) {
      return template.replace('$1', matched[1].replace(/_/g, '.'))
    }
  }
  return ''
}

function buildHeaders() {
  const ua = navigator.userAgent || ''
  const headers = {
    'X-Device-Id': safeHeaderValue(getStableDeviceId()),
    'X-Device-Type': detectDeviceType(ua),
    'X-Platform': 'web',
    'X-OS-Version': safeHeaderValue(detectOsVersion(ua))
  }

  Object.keys(headers).forEach((key) => {
    if (!headers[key]) {
      delete headers[key]
    }
  })

  return headers
}

/**
 * 获取设备请求头（构造一次后缓存）
 * @returns {Object}
 */
export function getDeviceHeaders() {
  if (!cachedHeaders) {
    try {
      cachedHeaders = buildHeaders()
    } catch (e) {
      // 采集失败不能阻塞请求，服务端仍可从 IP + User-Agent 兜底溯源
      console.warn('构造设备请求头失败', e)
      cachedHeaders = {}
    }
  }

  const headers = { ...cachedHeaders }

  // 网络类型属于可选信息，部分浏览器不支持 NetworkInformation
  try {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (connection && connection.effectiveType) {
      headers['X-Network-Type'] = connection.effectiveType
    }
  } catch (e) {
    // ignore
  }

  return headers
}

export default {
  getDeviceHeaders,
  getStableDeviceId
}
